import { graphql } from 'app/generated/gql'
import { useEffect, useState } from 'react'
import { DEFAULT_APPEARANCE } from 'app/lib/forms/appearance'
import type { Answer, FormDefinition } from 'app/lib/forms/types'
import type { PacketContent } from './PacketRunner'
import { LinkProblemScreen } from './LinkProblemScreen'
import { PacketRunner } from './PacketRunner'
import { LINK_STATE_PROBLEM, type LinkProblem, type Practice } from 'app/lib/forms/paperworkLink'
import Spinner from 'app/components/Spinner'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { debug, logError } from 'lib/logging'

// The API's frozen snapshot shape. Each form carries formId + formVersion; the runtime wants
// id + version, so we adapt below.
type SnapshotForm = {
  formId: string
  formVersion: number
  name: string
  description?: string
  category: FormDefinition['category']
  questions: FormDefinition['questions']
}

type PaperworkSnapshot = {
  name: string
  branding?: { displayName: string; logoUrl?: string; phone?: string }
  appearance?: PacketContent['appearance']
  welcome?: { title: string; body: string }
  completion?: { title: string; body: string }
  forms: SnapshotForm[]
}

type PublicPaperwork = {
  linkState: string
  practice: Practice | null
  status: 'sent' | 'opened' | 'completed' | 'expired' | 'cancelled' | null
  expiresDate: string | null
  packet: PaperworkSnapshot | null
  submittedFormIds: string[] | null
}

const PAPERWORK_BY_TOKEN = graphql(`
  query PaperworkByToken($token: String!) {
    paperworkByToken(token: $token) {
      linkState
      practice {
        displayName
        phone
        logoUrl
      }
      status
      expiresDate
      packet
      submittedFormIds
    }
  }
`)

const SUBMIT_PAPERWORK_FORM = graphql(`
  mutation SubmitPaperworkForm($token: String!, $formId: ID!, $formVersion: Int!, $answers: [PublicAnswerInput!]!) {
    submitPaperworkForm(token: $token, formId: $formId, formVersion: $formVersion, answers: $answers) {
      linkState
      status
      submittedFormIds
    }
  }
`)

// The runtime's Answer.value is string | string[]; the API takes a string[] (a single value is a
// one-element array). Normalize on the way out.
function toValueArray(value: Answer['value']): string[] {
  return Array.isArray(value) ? value : [value]
}

function snapshotToPacket(snapshot: PaperworkSnapshot): PacketContent {
  return {
    name: snapshot.name,
    welcome: snapshot.welcome,
    completion: snapshot.completion,
    appearance: snapshot.appearance ?? DEFAULT_APPEARANCE,
    branding: snapshot.branding ?? { displayName: snapshot.name },
    forms: snapshot.forms.map((f) => ({
      id: f.formId,
      name: f.name,
      description: f.description,
      category: f.category,
      version: f.formVersion,
      questions: f.questions,
    })),
  }
}

export function PaperworkRuntime({ token }: { token?: string }) {
  const [loading, setLoading] = useState(true)
  const [paperwork, setPaperwork] = useState<PublicPaperwork>()
  const [problem, setProblem] = useState<LinkProblem>()
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!token) return
    let active = true
    setLoading(true)
    setProblem(undefined)
    gqlQuery<{ paperworkByToken: PublicPaperwork }>(PAPERWORK_BY_TOKEN, { token }, { suppressErrorToast: true })
      .then((res) => {
        if (!active) return
        const loaded = res.data?.paperworkByToken
        if (res.error) setProblem(`unreachable`)
        else if (!loaded) setProblem(`invalid`)
        else {
          setPaperwork(loaded)
          setProblem(LINK_STATE_PROBLEM[loaded.linkState])
        }
      })
      .catch((e) => {
        logError('failed to load paperwork', e)
        if (active) setProblem(`unreachable`)
      })
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [token, attempt])

  if (loading) {
    return (
      <div className="flex h-full min-h-screen items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (problem || !paperwork?.packet) {
    return (
      <LinkProblemScreen
        problem={problem ?? `invalid`}
        practice={paperwork?.practice ?? undefined}
        onRetry={() => setAttempt((n) => n + 1)}
      />
    )
  }

  const snapshot = paperwork.packet
  const packet = snapshotToPacket(snapshot)

  return (
    <PacketRunner
      packet={packet}
      onSubmitForm={async (formId, answers) => {
        if (!token) throw new Error(`Paperwork token is required`)
        const form = snapshot.forms.find((f) => f.formId === formId)
        if (!form) throw new Error(`Unknown form ${formId}`)
        const res = await gqlMutation(
          SUBMIT_PAPERWORK_FORM,
          {
            token,
            formId,
            formVersion: form.formVersion,
            answers: answers.map((a: Answer) => ({ questionId: a.questionId, value: toValueArray(a.value) })),
          },
          { suppressErrorToast: true },
        )
        if (res.error) throw new Error(res.error.message)
        const blocked = LINK_STATE_PROBLEM[res.data?.submitPaperworkForm.linkState ?? ``]
        if (blocked) {
          setProblem(blocked)
          throw new Error(`Paperwork link is ${blocked}`)
        }
        debug('forms', 'paperwork form submitted', { formId, status: res.data?.submitPaperworkForm.status })
      }}
      // PacketRunner shows its own branded completion screen after the last form; nothing to do.
      onComplete={() => debug('forms', 'paperwork complete')}
    />
  )
}
